import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    getUnreadCount,
    listNotifications,
    markAllNotificationsRead,
    markNotificationRead,
} from "../services/notifications/notificationsApi";
import { getEcho } from "../services/notifications/echoClient";

function getToken() {
    // your rule
    const raw = localStorage.getItem("token") || "";
    const token = raw.startsWith("Bearer ") ? raw.slice(7) : raw;
    return token || null;
}

function getAdminId() {
    // we will store it on login
    const raw = localStorage.getItem("admin_id");
    const id = raw ? Number(raw) : null;
    return Number.isFinite(id) && id > 0 ? id : null;
}

export function useNotifications() {
    const token = getToken();
    const adminId = getAdminId();
    const isAdminSession = Boolean(token && adminId);

    const [unreadCount, setUnreadCount] = useState(0);
    const [items, setItems] = useState([]);
    const [loadingList, setLoadingList] = useState(false);

    const subscribedRef = useRef(false);

    const refreshUnread = useCallback(async () => {
        if (!isAdminSession) return;

        try {
            const res = await getUnreadCount({ token });
            const count = Number(res?.unread_count ?? 0);
            setUnreadCount(Number.isFinite(count) ? count : 0);
        } catch {
            // ignore
        }
    }, [isAdminSession, token]);

    const load = useCallback(
        async ({ perPage = 20, unreadOnly = false } = {}) => {
            if (!isAdminSession) return;

            setLoadingList(true);
            try {
                const res = await listNotifications({ perPage, unreadOnly, token });
                const data = Array.isArray(res?.data) ? res.data : [];
                setItems(data);
            } finally {
                setLoadingList(false);
            }
        },
        [isAdminSession, token]
    );

    const markRead = useCallback(
        async (id) => {
            if (!isAdminSession) return;

            // optimistic
            setItems((prev) =>
                prev.map((n) =>
                    n.id === id ? { ...n, read_at: n.read_at || new Date().toISOString() } : n
                )
            );
            setUnreadCount((c) => Math.max(0, c - 1));

            try {
                await markNotificationRead(id, { token });
            } catch {
                await refreshUnread();
            }
        },
        [isAdminSession, token, refreshUnread]
    );

    const markAllRead = useCallback(async () => {
        if (!isAdminSession) return;

        setUnreadCount(0);
        setItems((prev) =>
            prev.map((n) => ({ ...n, read_at: n.read_at || new Date().toISOString() }))
        );

        try {
            await markAllNotificationsRead({ token });
        } catch {
            await refreshUnread();
        }
    }, [isAdminSession, token, refreshUnread]);

    // initial unread count
    useEffect(() => {
        refreshUnread();
    }, [refreshUnread]);

    // realtime subscription (soketi)
    useEffect(() => {
        if (!isAdminSession) {
            // keep it silent in prod; change to console.debug if needed
            // console.debug("[Notifications] realtime skipped", { hasToken: !!token, adminId });
            return;
        }

        if (subscribedRef.current) return;

        subscribedRef.current = true;

        const echo = getEcho({ token });
        const channelName = `notifications.${adminId}`;

        const channel = echo.private(channelName);

        channel.listen(".crm.notification.created", (payload) => {
            if (!payload?.id) return;

            setUnreadCount((c) => c + 1);
            setItems((prev) => (prev.some((x) => x.id === payload.id) ? prev : [payload, ...prev]));

            try {
                if (typeof window !== "undefined") {
                    window.dispatchEvent(new CustomEvent("crm.notification.created", { detail: payload }));
                }
            } catch {
                // ignore
            }
        });

        return () => {
            try {
                echo.leave(channelName);
            } catch {
                // ignore
            }
            subscribedRef.current = false;
        };
    }, [isAdminSession, token, adminId]);

    const hasNew = unreadCount > 0;

    return useMemo(
        () => ({
            token,
            adminId,
            unreadCount,
            hasNew,
            items,
            loadingList,
            refreshUnread,
            load,
            markRead,
            markAllRead,
        }),
        [token, adminId, unreadCount, hasNew, items, loadingList, refreshUnread, load, markRead, markAllRead]
    );
}
